import { DialogContent } from '@mui/material'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import _ from 'lodash'
import React, { useMemo, useState } from 'react'
import FlipMove from 'react-flip-move'
import VariantDetails from './VariantDetails'
import { ISupplyDataVariant, ISupplyFormData } from '@components/Product/VariantKitPanel/VariantKitPanel'
import { Formik } from 'formik'
import * as yup from 'yup'
import FormattedPrice from '@components/FormattedPrice'
import { useTranslation } from 'react-i18next'

const schema = yup.object().shape({
  supplyLine: yup.number().nullable(),
  variants: yup
    .array()
    .of(
      yup.object().shape({
        pk: yup.number().required(),
        supplyProductPk: yup.number().required(),
        quantity: yup.number().required(),
      })
    )
    .min(1),
})

interface FormData {
  supplyLine: number | null
  variants: { pk: number; quantity: number; supplyProductPk: number }[]
}

const getInitialSupplyProducts = (supplyProducts: ISupplyFormData['supplyData'], editMode = false) => {
  if (!editMode)
    return _.sortBy(
      [...supplyProducts],
      supplyProduct =>
        !supplyProduct.variants.find(v => v.availability || ['in', 'backorder'].includes(v.outOfStockStatus))
    )
  else {
    const isInCartProducts = supplyProducts.filter(({ variants }) => variants.find(variant => variant.isInCart))
    const notIsInCart = supplyProducts.filter(({ variants }) => !variants.find(variant => variant.isInCart))
    return [...isInCartProducts, ...notIsInCart]
  }
}

const findSupplyVariantById = (supplyProducts: ISupplyFormData['supplyData'], id: number): ISupplyDataVariant => {
  let v: ISupplyDataVariant

  supplyProducts.forEach(({ variants }) =>
    variants.forEach(variant => {
      if (variant.supplyVariantPk === id) {
        v = variant
      }
    })
  )
  return v
}

interface IProps {
  data: ISupplyFormData
  onSubmit(values: FormData): Promise<void>
  onClosePanel(): void
  isWholesale: boolean
}

const VariantKitForm: React.FC<IProps> = ({ data, onSubmit, onClosePanel, isWholesale }) => {
  const { t } = useTranslation()
  const [supplyProducts, setSupplyProducts] = useState(getInitialSupplyProducts(data.supplyData, !!data.cartLinePk))

  const findVariantIndex = (supplyProductPk: number) => {
    return _.findIndex(supplyProducts, ({ variants }) => {
      const i = _.findIndex(variants, variant => variant.supplyProductPk === supplyProductPk)
      return i > -1
    })
  }

  const moveToBottom = (supplyProductPk: number) => {
    const index = findVariantIndex(supplyProductPk)
    let res = supplyProducts.slice()
    if (index > -1 && index !== res.length - 1) {
      res = [...res.slice(0, index), ...res.slice(index + 1), res[index]]
    }
    setSupplyProducts(res)
  }
  const moveTop = (supplyProductPk: number) => {
    const index = findVariantIndex(supplyProductPk)
    let res = supplyProducts.slice()
    if (index > 0) {
      res = [res[index], ...res.slice(0, index), ...res.slice(index + 1)]
    }
    setSupplyProducts(res)
  }

  const initialValues: FormData = useMemo(() => {
    const values: FormData = {
      supplyLine: data.cartLinePk,
      variants: [],
    }
    data.supplyData.forEach(({ variants }) => {
      const initialVariant = variants.find(el => el.isInCart) || variants[0]
      const quantity = initialVariant.isInCart
        ? initialVariant.cartQuantity
        : !initialVariant.cartLine &&
          (initialVariant.stockQty || ['in', 'backorder'].includes(initialVariant.outOfStockStatus))
        ? 1
        : 0
      if (quantity > 0) {
        values.variants.push({
          pk: initialVariant.supplyVariantPk,
          supplyProductPk: initialVariant.supplyProductPk,
          quantity: quantity,
        })
      }
    })
    return values
  }, [JSON.stringify(data)])

  return (
    <Formik<FormData>
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={values => {
        const ids = data.supplyData.map(({ variants }) => variants.map(variant => variant.supplyVariantPk)).flat()
        const sortedVariants = _.sortBy(values.variants, variant => ids.indexOf(variant.pk))
        onSubmit({ ...values, variants: sortedVariants })
      }}
      enableReinitialize
      validateOnMount
    >
      {form => {
        const removeVariant = (supplyVariantPk: number) => {
          form.setFieldValue(
            'variants',
            [...form.values.variants].filter(o => o.pk !== supplyVariantPk)
          )
        }
        const addVariant = (supplyVariantPk: number, data: FormData['variants'][0]) => {
          const res = [...form.values.variants].filter(o => o.pk !== supplyVariantPk)
          res.push(data)
          form.setFieldValue('variants', res)
        }
        const itemsSelectedCount = form.values.variants.reduce((prev, curr) => prev + curr.quantity, 0)
        const itemsSelectedPrice = form.values.variants.reduce((prev, curr) => {
          const v = findSupplyVariantById(supplyProducts, curr.pk)
          return prev + curr.quantity * Number(isWholesale ? v?.wholesalePrice.gross || 0 : v?.price.gross || 0)
        }, 0)

        const currency = supplyProducts[0]?.supplyProduct?.currency || 'USD'
        return (
          <>
            <DialogContent className='w-full max-w-full' dividers>
              <div>
                <div>
                  <div>{t('description.dialogCustomizeKit', "Exclude the item you don't want to see in the kit")}</div>
                  <div className='mb-20 mt-10'>
                    <Divider />
                  </div>
                  <FlipMove enterAnimation='fade' leaveAnimation='fade'>
                    {supplyProducts.map(({ supplyProduct, variants }) => {
                      return (
                        <div key={supplyProduct.id}>
                          <Grid container spacing={1}>
                            <VariantDetails
                              variants={variants}
                              supplyProduct={supplyProduct}
                              onClosePanel={onClosePanel}
                              removeVariant={removeVariant}
                              addVariant={addVariant}
                              moveToBottom={moveToBottom}
                              moveTop={moveTop}
                              isWholesale={isWholesale}
                            />
                          </Grid>
                          <div className='my-15'>
                            <Divider />
                          </div>
                        </div>
                      )
                    })}
                  </FlipMove>
                </div>
              </div>
            </DialogContent>
            <DialogActions style={{ padding: '8px 24px' }}>
              <div className='w-full' style={{ backgroundColor: '#fff' }}>
                <div className='flex items-center justify-between uppercase font-semibold text-lg pb-10'>
                  <div>
                    {t('itemsAdded', 'Items Added')}: {itemsSelectedCount}
                  </div>
                  <div>
                    {t('total', 'Total')}: <FormattedPrice amount={itemsSelectedPrice} currency={currency} />
                  </div>
                </div>
                <div className='flex flex-wrap items-center w-full -mx-2'>
                  <span className='flex-1 px-2'>
                    <Button variant={'outlined'} color={'primary'} onClick={onClosePanel} fullWidth>
                      {t('button.cancel', 'Cancel')}
                    </Button>
                  </span>
                  <span className='flex-1 min-w-200 px-2'>
                    <Button
                      type='submit'
                      disabled={!form.isValid}
                      variant='contained'
                      color='primary'
                      onClick={() => form.handleSubmit()}
                      fullWidth
                    >
                      {!form.isValid
                        ? t('button.chooseProduct', 'Choose product')
                        : !data.cartLinePk
                        ? t('button.saveAndAddToBag', 'Save and add to bag')
                        : t('button.updateKit', 'Update the kit')}
                    </Button>
                  </span>
                </div>
              </div>
            </DialogActions>
          </>
        )
      }}
    </Formik>
  )
}

export default VariantKitForm
